import { FC, useCallback, useEffect, useState, useMemo } from 'react';
import { Stack, TextField } from '@fluentui/react';
import { AddRegular, PreviousFilled, NextFilled } from '@fluentui/react-icons';
import { jobRolesApi } from '../../api/api';
import styles from './styles.module.css';
import MasterList from './MasterList';
import { JobRole } from '../../api';
import Button from '../../components/Button';
import EditJobRoleModal from './components/EditJobRoleModal';
import JobRolesTable from './components/JobRolesTable';

const JobRoles: FC = () => {
  const [jobRoles, setJobRoles] = useState<JobRole[]>([]);
  const [showAddRole, setShowAddRole] = useState<boolean>(false);
  const [newJobRole, setNewJobRole] = useState<JobRole>();
  const [page, setPage] = useState<number>(1);
  const [search, setSearch] = useState<string | undefined>(undefined);
  const [pageSize, setPageSize] = useState<number>(10);

  const loadRoles = useCallback(async () => {
    const roles = await jobRolesApi.fetchAll();
    setJobRoles(roles);
  }, []);

  useEffect(() => {
    loadRoles();
  }, [loadRoles]);

  const deleteRole = useCallback(
    async (jobRole: JobRole) => {
      await jobRolesApi.deleteOne({ jobRole });
      loadRoles();
    },
    [loadRoles],
  );

  const editRole = useCallback((jobRole: JobRole) => {
    setNewJobRole(jobRole);
    setShowAddRole(true);
  }, []);

  const searchedRoles = useMemo(() => {
    if (!jobRoles) return [];

    const searched = search
      ? jobRoles.filter(role => Object.values(role).join(' ').toLowerCase().includes(search))
      : jobRoles;

    return searched;
  }, [search, jobRoles]);

  const pages = useMemo(() => {
    const calcpages = searchedRoles.length === 0 ? 1 : Math.ceil(searchedRoles.length / pageSize);

    if (calcpages < page) {
      setPage(calcpages);
    }

    return calcpages;
  }, [searchedRoles, pageSize, page]);

  const paginatedRoles = useMemo(() => {
    return searchedRoles.slice((page - 1) * pageSize, page * pageSize);
  }, [searchedRoles, page, pageSize]);

  const closeAddRole = useCallback(() => {
    setShowAddRole(false);
    setNewJobRole(undefined);
  }, []);

  const addRole = useCallback(
    async (jobRole: JobRole) => {
      await jobRolesApi.upsertOne({ jobRole });
      closeAddRole();
      loadRoles();
    },
    [closeAddRole, loadRoles],
  );

  const changePageSize = useCallback((event: any, option?: any) => {
    if (option.key === undefined) return;
    setPageSize(parseInt(option.key));
  }, []);

  return (
    <MasterList selectedTab="Job Roles">
      <Stack horizontal style={{ justifyContent: 'space-between' }}>
        <Stack.Item>
          <TextField
            className={styles.textNarrow}
            type="text"
            placeholder="Search"
            onChange={evt => setSearch((evt.target as any).value)}
          />
        </Stack.Item>
        <Stack.Item>
          <Button label="Previous" icon={PreviousFilled} onClick={() => setPage(page - 1)} disabled={page === 1} />
        </Stack.Item>
        <Stack.Item>
          <span>
            Page {page} of {pages}
          </span>
        </Stack.Item>
        <Stack.Item>
          <Button label="Next" icon={NextFilled} onClick={() => setPage(page + 1)} disabled={page >= pages} />
        </Stack.Item>
        <Stack.Item>
          <Button label="Add a New Role" icon={AddRegular} onClick={() => setShowAddRole(true)} />
        </Stack.Item>
      </Stack>
      <Stack style={{ marginTop: '16px' }}>
        <JobRolesTable jobRoles={paginatedRoles} onEdit={editRole} onDelete={deleteRole} />
      </Stack>
      <EditJobRoleModal isOpen={showAddRole} jobRole={newJobRole} onSubmit={addRole} onCancel={closeAddRole} />
    </MasterList>
  );
};

export default JobRoles;
